import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface HeroProps {
  scrolled: boolean;
}

export default function Hero({ scrolled }: HeroProps) {
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMenuOpen(false);
  };

  const handleNavClick = (item: string) => {
    if (item === 'SKI TRIKKE') {
      navigate('/ski-trikke');
      setMenuOpen(false);
      return;
    }
    const sectionId = item.toLowerCase().replace(' ', '-');
    scrollToSection(sectionId);
  };

  return (
    <section id="home" className="relative min-h-screen w-full overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/a8c3d3e584e8aa9f23e2ae82973c6774/adc836ba8f26084eccf551afc1c4a1b9.jpeg"
          alt="Chalet Lac de Vonnes at night, Châtel"
          className="w-full h-full object-cover object-center"
          style={{ minHeight: '100%' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/40"></div>
      </div>

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-white shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="w-full px-6 md:px-12 lg:px-24 py-4 md:py-6 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollToSection('home')}>
            <img
              src="https://www.dropbox.com/scl/fi/f07ifs1yd05cfcl5cs6mt/234187158.png?rlkey=vl3eoprhczi5ajtexxf2qds9b&st=fmwe88ls&raw=1"
              alt="Vonnes Alpine Life Logo"
              className={`h-9 md:h-12 w-auto object-contain transition-all duration-500 ${
                scrolled ? 'opacity-100 brightness-100' : 'opacity-75 brightness-150'
              }`}
            />
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center gap-8 xl:gap-12">
            {['HOME', 'ABOUT', 'CHALET', 'SKI TRIKKE', 'GALLERY', 'CONTACT'].map((item, index) => (
              <button
                key={index}
                onClick={() => handleNavClick(item)}
                onMouseEnter={() => setHoveredLink(item)}
                onMouseLeave={() => setHoveredLink(null)}
                className={`text-sm font-light tracking-widest transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? hoveredLink === item
                      ? 'text-gray-900 opacity-100'
                      : 'text-gray-600 opacity-80'
                    : hoveredLink === item
                    ? 'text-white opacity-100'
                    : 'text-white opacity-60'
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`text-2xl ${scrolled ? 'text-gray-900' : 'text-white'} ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {menuOpen && (
          <div className="lg:hidden bg-white shadow-xl border-t border-gray-100">
            {['HOME', 'ABOUT', 'CHALET', 'SKI TRIKKE', 'GALLERY', 'CONTACT'].map((item, index) => (
              <button
                key={index}
                onClick={() => handleNavClick(item)}
                className="w-full text-left px-6 py-4 text-sm font-light tracking-widest text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-50"
              >
                {item}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 min-h-screen flex flex-col justify-center px-6 md:px-12 lg:px-24 py-32">
        <div className="max-w-4xl" style={{ marginTop: '-5%' }}>
          {/* Brand label above headline */}
          <p
            className="text-lg md:text-2xl lg:text-3xl font-light tracking-[0.35em] text-white/80 mb-5 md:mb-7 uppercase"
            style={{ fontFamily: 'Cormorant Garamond, serif', letterSpacing: '0.4em' }}
          >
            Vonnes Alpine Life
          </p>
          <h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-thin tracking-wider text-white mb-4 md:mb-6 leading-tight"
            style={{ letterSpacing: '2px' }}
          >
            ELEVATE YOUR
            <br />
            ALPINE EXPERIENCE
          </h1>
          <p
            className="text-xl md:text-2xl lg:text-3xl font-light mb-8 md:mb-16"
            style={{ fontFamily: 'Cormorant Garamond, serif', color: '#D4AF37' }}
          >
            Luxury chalet & premium ski equipment in Châtel
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <button
              onClick={() => scrollToSection('chalets')}
              className="group flex items-center gap-3 bg-white text-black px-7 py-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <span className="text-base font-light tracking-wide">Explore Our Chalet</span>
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-black text-white group-hover:bg-gray-800 transition-colors">
                <i className="ri-arrow-right-line text-lg"></i>
              </div>
            </button>

            <button
              onClick={() => scrollToSection('ski-trikke-explore')}
              className="group flex items-center gap-3 px-7 py-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap border border-white/60"
              style={{ backgroundColor: 'rgba(212,175,55,0.15)', backdropFilter: 'blur(8px)' }}
            >
              <span className="text-base font-light tracking-wide text-white">Explore Skki Trikke</span>
              <div className="w-9 h-9 flex items-center justify-center rounded-full text-white group-hover:opacity-80 transition-colors" style={{ backgroundColor: '#D4AF37' }}>
                <i className="ri-arrow-right-line text-lg"></i>
              </div>
            </button>
          </div>
        </div>

        {/* Location Badge */}
        <div className="absolute bottom-8 right-6 md:right-24 flex items-center gap-3 text-white">
          <i className="ri-map-pin-line text-lg"></i>
          <span className="text-sm font-light tracking-wide">Châtel, French Alps</span>
        </div>
      </div>
    </section>
  );
}
